/**
    * @description      : 
    * @author           : USER
    * @group            : 
    * @created          : 15/11/2025 - 14:22:12
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 15/11/2025
    * - Author          : USER
    * - Modification    : 
**/
import axios from 'axios';
import { getClerkAuthState, waitForClerkToken } from './clerkToken';

const getApiBaseUrl = () => {
  const configuredUrl = (process.env.REACT_APP_API_URL || '').trim();

  if (configuredUrl && configuredUrl !== 'undefined') {
    return configuredUrl.replace(/\/$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return 'http://localhost:8000';
};

const API_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {'Content-Type': 'application/json'},
});

// Try to get Clerk token, but don't crash if it's not available
api.interceptors.request.use(async (config) => {
  const nextConfig = { ...config };
  nextConfig.headers = nextConfig.headers || {};

  try {
    const token = await waitForClerkToken();
    if (token) {
      nextConfig.headers.Authorization = `Bearer ${token}`;
    } else {
      delete nextConfig.headers.Authorization;
    }
  } catch (err) {
    // Silently fail - backend will handle unauthenticated requests
    console.log('No auth token available');
  }
  return nextConfig;
});

// Handle errors gracefully
api.interceptors.response.use(
  response => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const authState = getClerkAuthState();

    // Also retry if window.Clerk.session exists — handles subdomain identity crisis
    // where useAuth().isSignedIn is false on chat.tuliza.org but session is active.
    const hasWindowSession =
      typeof window !== 'undefined' && !!window.Clerk?.session;

    if (
      originalRequest &&
      !originalRequest._authRetried &&
      (status === 401 || status === 403) &&
      (authState.isSignedIn || !authState.isLoaded || hasWindowSession)
    ) {
      originalRequest._authRetried = true;

      const refreshedToken = await waitForClerkToken({
        retries: authState.isSignedIn ? 6 : 10,
        delayMs: 300,
        skipCache: true,
      });

      if (refreshedToken) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${refreshedToken}`;
        return api.request(originalRequest);
      }
    }

    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// ============================================
// CHAT ENDPOINTS
// ============================================

export const getChats = async () => {
  try {
    const response = await api.get('/chatbot/chats/');
    return response.data;
  } catch (error) {
    console.error('getChats error:', error);
    return [];
  }
};

export const createChat = async (title = null, messages = null) => {
  const payload = {};
  if (title) payload.title = title;
  if (messages) payload.messages = messages;
  const response = await api.post('/chatbot/chats/', payload);
  return response.data;
};

export const saveAnonymousChat = async (messages, chatTitle = 'Preserved Chat') => {
  const response = await api.post('/chatbot/chats/save_anonymous_chat/', {
    messages,
    title: chatTitle
  });
  return response.data;
};

export const deleteChat = async (chatId) => {
  await api.delete(`/chatbot/chats/${chatId}/`);
};

export const getChatMessages = async (chatUrlCode) => {
  const response = await api.get(`/chatbot/chats/${chatUrlCode}/messages/`);
  return response.data;
};

export const sendMessage = async (chatUrlCode, message, language = 'en', model = 'flash') => {
  const response = await api.post(`/chatbot/chats/${chatUrlCode}/send_message/`, {
    message,
    language,
    model
  });
  return response.data;
};

export const sendAnonymousMessage = async (message, language = 'en', model = 'flash', history = []) => {
  const response = await api.post('/chatbot/chats/send_anonymous_message/', {
    message,
    language,
    model,
    history
  });
  return response.data;
};

export const sendQuickMessage = async (message, language = 'en', history = []) => {
  const response = await api.post('/chatbot/chats/quick_response/', {
    message,
    language,
    history
  });
  return response.data;
};

export const streamMessage = async (message, language = 'en', history = [], chatUrlCode = null, onChunk, onDone, onError, onUserMessage, onTitleUpdate) => {
  const API_URL = getApiBaseUrl();

  // Get auth token
  let headers = { 'Content-Type': 'application/json' };
  try {
    const token = await waitForClerkToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  } catch (e) {}

  const maxRetries = 3;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    let response;
    try {
      response = await fetch(`${API_URL}/api/chatbot/chats/stream_message/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message, language, history, chat_url_code: chatUrlCode })
      });
    } catch (fetchErr) {
      if (attempt < maxRetries) {
        await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
        continue;
      }
      if (onError) onError('Network error. Please check your connection.');
      return;
    }

    const clerkState = getClerkAuthState();
    const hasWinSession = typeof window !== 'undefined' && !!window.Clerk?.session;
    const shouldRetryAuth =
      (response.status === 401 || response.status === 403) &&
      (clerkState.isSignedIn || hasWinSession);

    // Retry on auth warmup, 429 rate limit, or 502/503 gateway errors
    if ((response.status === 429 || response.status === 502 || response.status === 503 || shouldRetryAuth) && attempt < maxRetries) {
      if (shouldRetryAuth) {
        const refreshedToken = await waitForClerkToken({
          retries: 3,
          delayMs: 300,
          skipCache: true,
        });
        if (refreshedToken) {
          headers = {
            ...headers,
            Authorization: `Bearer ${refreshedToken}`,
          };
        }
      }

      const delay = response.status === 429
        ? 1500 * Math.pow(2, attempt)
        : shouldRetryAuth
          ? 350 * (attempt + 1)
          : 2000 * (attempt + 1);
      await new Promise(r => setTimeout(r, delay));
      continue;
    }

    if (!response.ok) {
      try {
        const errText = await response.text();
        if (onError) onError(`${response.status} ${response.statusText}. ${errText}`);
      } catch (e) {
        if (onError) onError(`Server error: ${response.status} ${response.statusText}`);
      }
      return;
    }

    // Success - read the stream
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'chunk' && onChunk) onChunk(event.data);
              else if (event.type === 'done' && onDone) onDone(event.data);
              else if (event.type === 'user_message' && onUserMessage) onUserMessage(event.data);
              else if (event.type === 'title_update' && onTitleUpdate) onTitleUpdate(event.data);
              else if (event.type === 'error') {
                // Check if it's a 429 inside the stream
                if (String(event.data).includes('429') && attempt < maxRetries) {
                  reader.cancel();
                  await new Promise(r => setTimeout(r, 1500 * Math.pow(2, attempt)));
                  break; // Break inner loop to retry
                }
                if (onError) onError(event.data);
                return;
              }
            } catch (e) {}
          }
        }
      }
    } catch (streamErr) {
      if (attempt < maxRetries) {
        await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
        continue;
      }
      if (onError) onError('Stream interrupted. Please try again.');
      return;
    }

    return; // Success, done
  }
};

// ============================================
// DOCUMENT ENDPOINTS
// ============================================

export const uploadDocument = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/documents/', formData, {
    headers: {'Content-Type': 'multipart/form-data'},
  });
  return response.data;
};

export const getDocuments = async () => {
  try {
    const response = await api.get('/documents/');
    return response.data;
  } catch (error) {
    console.error('getDocuments error:', error);
    return [];
  }
};

export const deleteDocument = async (docId) => {
  await api.delete(`/documents/${docId}/`);
};

export const scrapeUrl = async (url, title = '') => {
  const response = await api.post('/documents/scrape-url/', {
    url,
    title
  });
  return response.data;
};

export const scrapeMultipleUrls = async (urls) => {
  const response = await api.post('/documents/scrape-urls/', {
    urls
  });
  return response.data;
};

// ============================================
// USER PROFILE ENDPOINTS
// ============================================

export const getUserProfile = async () => {
  try {
    const response = await api.get('/users/profile/');
    return response.data;
  } catch (error) {
    console.error('getUserProfile error:', error);
    return null;
  }
};

export const updateProfile = async (data) => {
  const response = await api.patch('/users/profile/', data);
  return response.data;
};

export const changePassword = async (currentPassword, newPassword) => {
  const response = await api.post('/users/change-password/', {
    current_password: currentPassword,
    new_password: newPassword
  });
  return response.data;
};

// ============================================
// ANALYTICS ENDPOINTS
// ============================================

export const getAnalyticsDashboard = async () => {
  try {
    const response = await api.get('/analytics/dashboard/');
    return response.data;
  } catch (error) {
    console.error('getAnalyticsDashboard error:', error);
    return null;
  }
};

export const getAdminAnalytics = async () => {
  try {
    const response = await api.get('/analytics/admin_dashboard/');
    return response.data;
  } catch (error) {
    console.error('getAdminAnalytics error:', error);
    return null;
  }
};

export const trackActivity = async (activityType, metadata = {}) => {
  try {
    const response = await api.post('/analytics/track/', {
      activity_type: activityType,
      metadata,
    });
    return response.data;
  } catch (error) {
    console.error('trackActivity error:', error);
    return null;
  }
};

// ============================================
// FEEDBACK ENDPOINTS
// ============================================

export const sendMessageFeedback = async (messageId, rating, comment = '') => {
  const response = await api.post('/chatbot/feedback/', {
    message: messageId,
    rating: rating, // 'positive' or 'negative'
    comment: comment
  });
  return response.data;
};

export const removeFeedback = async (feedbackId) => {
  await api.delete(`/chatbot/feedback/${feedbackId}/`);
};

// ============================================
// TOKEN REFRESH (for future use)
// ============================================

export const refreshToken = async (refresh) => {
  const response = await api.post('/users/token/refresh/', {refresh});
  return response.data;
};

export default api;